// QuizView — MCQ + True/False drill with three phases: setup → running → finished.
//
// Decision history:
//   Session 19G (§5.6): three-phase state machine, A/B/C/D + 1234 keyboard.
//   Session 19H (§H2/§H4): bookmark toggle, active-session persistence, resume.
//   19BI: True/False support. URL ?kind=tf switches to TF queue.
//   Session 14 Step 3: state machine extracted to useQuizSession hook (~220 lines
//     out of view). View is now JSX rendering + keyboard handler only.
//   Session 50: phase renders + sub-components extracted to quiz-phases.tsx
//     (447→~130 lines).

import { useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ListChecks } from 'lucide-react';
import { useT } from '../i18n/index.js';
import { StateCard } from '../components/state-card.js';
import { useStoreCourseData } from '../lib/hooks/use-store-course.js';
import { shuffle } from '../lib/quiz-utils.js';
import { useQuizKeyboard } from '../lib/hooks/use-quiz-keyboard.js';
import { useEventBus } from '../lib/hooks/use-event-bus.js';
import {
  useQuizSession,
} from '../lib/hooks/use-quiz-session.js';
import type { QuizItem, QuizKind } from '../lib/hooks/use-quiz-session.js';
import {
  QuizSetupPhase,
  QuizRunningPhase,
  QuizFinishedPhase,
} from './quiz-phases.js';
import { useToast } from '../components/toast.js';

export function QuizView() {
  const t = useT();
  const navigate = useNavigate();
  const toast = useToast();
  const [params] = useSearchParams();
  const drillItems = params.get('items');
  const kindParam = (params.get('kind') ?? 'mcq') as QuizKind;
  const topicParam = params.get('topic');
  const caseParam = params.get('case');
  const { draft, pkg, progress, refreshProgress } = useStoreCourseData();
  const eventBus = useEventBus();

  // Build item pool based on kind.
  const allMcqs = pkg.mcqs;
  const allTfs = pkg.trueFalse ?? [];

  const itemPool = useMemo<QuizItem[]>(() => {
    if (kindParam === 'tf') {
      return allTfs.map((tf): QuizItem => ({ kind: 'tf', data: tf }));
    }
    return allMcqs.map((mcq): QuizItem => ({ kind: 'mcq', data: mcq }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kindParam, allMcqs.length, allTfs.length]);

  // Build deep-link queue when topic/case is in URL.
  const deepLinkQueue: QuizItem[] | null = useMemo(() => {
    if (drillItems) return null;
    if (topicParam) return shuffle(itemPool.filter((it) => {
      const d = it.data;
      return d.topicId === topicParam || d.subtopicId === topicParam;
    }));
    if (caseParam) return shuffle(itemPool.filter((it) => it.data.caseId === caseParam));
    return null;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drillItems, topicParam, caseParam, itemPool.length]);

  // ── Quiz session hook ───────────────────────────────────────────────────
  const quiz = useQuizSession(
    draft, progress, itemPool, eventBus, refreshProgress,
    drillItems, deepLinkQueue, caseParam,
  );
  const {
    phase, config, currentIdx, picked,
    totalQuestions, resolvedItem, isBookmarked,
    persistedSession, attempts,
    setConfig, startSession, resumeSession,
    abandonAndRestart, pickOption, nextOrFinish, toggleBookmark, restart,
  } = quiz;

  // ── Abandon confirm (uses view's t()) ───────────────────────────────────
  // 19BM-C: abandoning discards the resume session — sticky toast + action
  // replaces window.confirm.
  function handleAbandon(): void {
    toast.warn(t('quiz.resumeAbandonConfirm'), {
      sticky: true,
      action: {
        label: t('quiz.resumeAbandon'),
        onClick: () => { void abandonAndRestart(); },
      },
    });
  }

  // ── Keyboard shortcuts ──────────────────────────────────────────────────
  useQuizKeyboard(phase === 'running' && !!resolvedItem, {
    kind: resolvedItem?.kind ?? 'mcq',
    onPickOption: pickOption,
    onNext: () => { if (picked !== null) void nextOrFinish(); },
    onSubmit: () => { if (picked !== null) void nextOrFinish(); },
    onBookmark: () => { void toggleBookmark(); },
    mcqOptionCount: resolvedItem?.kind === 'mcq' ? resolvedItem.data.options.length : undefined,
  });

  // ── Empty state ─────────────────────────────────────────────────────────
  if (itemPool.length === 0) {
    return (
      <StateCard
      variant="empty"
      icon={<ListChecks size={48} />}
      title={t('quiz.empty')}
      hint={t('quiz.emptyHint')}
    />
    );
  }

  // ── Phase: setup ────────────────────────────────────────────────────────
  if (phase === 'setup') {
    return (
      <QuizSetupPhase
        t={t}
        kindParam={kindParam}
        pkg={pkg}
        config={config}
        setConfig={setConfig}
        persistedSession={persistedSession}
        onResume={resumeSession}
        onAbandon={handleAbandon}
        onStart={startSession}
      />
    );
  }

  // ── Phase: running ──────────────────────────────────────────────────────
  if (phase === 'running' && resolvedItem) {
    return (
      <QuizRunningPhase
        t={t}
        resolvedItem={resolvedItem}
        currentIdx={currentIdx}
        totalQuestions={totalQuestions}
        picked={picked}
        isBookmarked={isBookmarked}
        progress={progress}
        pkg={pkg}
        draft={draft}
        refreshProgress={refreshProgress}
        onPickOption={pickOption}
        onNextOrFinish={() => { void nextOrFinish(); }}
        onToggleBookmark={toggleBookmark}
      />
    );
  }

  // ── Phase: finished ─────────────────────────────────────────────────────
  if (phase === 'finished') {
    return (
      <QuizFinishedPhase
        t={t}
        navigate={navigate}
        attempts={attempts}
        onRestart={restart}
        courseId={draft.id}
        diagnosisVerdict={progress.diagnosis?.verdict ?? null}
      />
    );
  }

  return null;
}
